import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, FocusEvent, KeyboardEvent } from "react";
import { MapPin } from "lucide-react";
import { cn } from "../../lib/format";
import {
  createAddressSession,
  placesEnabled,
  type AddressParts,
  type AddressSuggestion,
} from "../../lib/googlePlaces";
import { TextField } from "./FormField";

type TextFieldProps = Parameters<typeof TextField>[0];

interface AddressFieldProps extends Omit<TextFieldProps, "full"> {
  /** Called with the split address once a suggestion is picked. */
  onAddressSelect: (parts: AddressParts) => void;
  full?: boolean;
}

const MIN_CHARS = 3;
const DEBOUNCE_MS = 250;

/**
 * A TextField that suggests addresses from Google Places as you type.
 * Spread react-hook-form's `register()` onto it like a TextField; when the
 * Maps API key is missing it behaves as a plain text input.
 */
export default function AddressField({
  onAddressSelect,
  full,
  onChange,
  onBlur,
  ...input
}: AddressFieldProps) {
  const session = useMemo(() => createAddressSession(), []);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState(false);
  const requestId = useRef(0);

  const searchable = placesEnabled && query.trim().length >= MIN_CHARS;

  useEffect(() => {
    const id = ++requestId.current;
    if (!searchable) return;
    const timer = setTimeout(() => {
      session
        .search(query)
        .then((list) => {
          if (id !== requestId.current) return; // a newer query won
          setSuggestions(list);
          setActive(-1);
        })
        .catch(() => setSuggestions([]));
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, searchable, session]);

  const pick = async (s: AddressSuggestion) => {
    setOpen(false);
    setSuggestions([]);
    requestId.current++;
    try {
      onAddressSelect(await s.resolve());
    } catch {
      // Lookup failed: the user can still fill the fields by hand.
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e);
    setQuery(e.target.value);
    setOpen(true);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
    setOpen(false);
  };

  const showList = open && searchable && suggestions.length > 0;

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showList) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (i + step + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault(); // don't submit the form
      pick(suggestions[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const listId = `${input.name ?? "address"}-suggestions`;

  return (
    <div className={cn("relative", full && "sm:col-span-2")}>
      <TextField
        {...input}
        autoComplete={placesEnabled ? "off" : input.autoComplete}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute inset-x-0 top-full z-20 m-0 mt-1 list-none overflow-hidden rounded border border-line bg-white p-1 shadow-lg"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              // mousedown, so it fires before the input's blur closes the list
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              onMouseEnter={() => setActive(i)}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded px-3 py-2.5 text-sm text-ink",
                i === active && "bg-surface",
              )}
            >
              <MapPin size={16} className="shrink-0 text-muted" />
              <span className="truncate">{s.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
